import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Spinner,
  Table,
  Tabs,
  Text,
  VStack,
  Separator
} from '@chakra-ui/react';
import { FaEdit, FaMagic, FaSave, FaTimes, FaTrash, FaRobot } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import countryService from '../services/countryService';
import shortnameService from '../services/shortnameService';

const MasterDataManager = () => {
  const { hasRole } = useAuth();

  const isAdmin = hasRole(['Admin']);
  const canManageMainShortnames = hasRole(['Admin', 'Editor']);
  const canManageBranchShortnames = hasRole(['Admin', 'Editor Branch']);
  const canManageCountries = hasRole(['Admin', 'Editor']);
  const canViewCountries = hasRole(['Admin', 'Editor', 'Editor Branch']);

  const defaultTab = useMemo(() => {
    if (canManageMainShortnames) return 'main-shortnames';
    if (canManageBranchShortnames) return 'branch-shortnames';
    return 'countries';
  }, [canManageMainShortnames, canManageBranchShortnames]);

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const [mainSearch, setMainSearch] = useState('');
  const [branchSearch, setBranchSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');

  const [mainShortnames, setMainShortnames] = useState([]);
  const [branchShortnames, setBranchShortnames] = useState([]);
  const [countries, setCountries] = useState([]);

  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [generatingBatch, setGeneratingBatch] = useState(false);

  const [editingMainId, setEditingMainId] = useState(null);
  const [editingMainValue, setEditingMainValue] = useState('');

  const [editingBranchId, setEditingBranchId] = useState(null);
  const [editingBranchValue, setEditingBranchValue] = useState('');

  const [newCountry, setNewCountry] = useState({ name: '', code: '' });
  const [editingCountryId, setEditingCountryId] = useState(null);
  const [editingCountry, setEditingCountry] = useState({ name: '', code: '' });

  // AI Logs & Prompts state
  const [aiLogs, setAiLogs] = useState([]);
  const [aiLogsTotal, setAiLogsTotal] = useState(0);
  const [aiLogsPage, setAiLogsPage] = useState(1);
  const [aiLogsSearch, setAiLogsSearch] = useState('');
  const [aiLogsStatus, setAiLogsStatus] = useState('all');
  const [aiLogsLoading, setAiLogsLoading] = useState(false);
  const [aiPrompts, setAiPrompts] = useState([]);
  const [editingPromptKey, setEditingPromptKey] = useState(null);
  const [editingPromptText, setEditingPromptText] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  const PAGE_SIZE_OPTIONS = [10, 20, 50];
  const [mainPage, setMainPage] = useState(1);
  const [branchPage, setBranchPage] = useState(1);
  const [countryPage, setCountryPage] = useState(1);
  const [mainPageSize, setMainPageSize] = useState(20);
  const [branchPageSize, setBranchPageSize] = useState(20);
  const [countryPageSize, setCountryPageSize] = useState(20);

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  const loadMainShortnames = useCallback(async (q = '') => {
    if (!canManageMainShortnames) return;
    const result = await shortnameService.getMainShortnames(q);
    setMainShortnames(result);
  }, [canManageMainShortnames]);

  const loadBranchShortnames = useCallback(async (q = '') => {
    if (!canManageBranchShortnames) return;
    const result = await shortnameService.getBranchShortnames(q);
    setBranchShortnames(result);
  }, [canManageBranchShortnames]);

  const loadCountries = useCallback(async () => {
    if (!canViewCountries) return;
    const result = await countryService.getAllCountries(false);
    setCountries(result);
  }, [canViewCountries]);

  const loadAiLogs = useCallback(async (q = '', status = 'all', page = 1) => {
    if (!isAdmin) return;
    setAiLogsLoading(true);
    try {
      const result = await shortnameService.getAiLogs({ q, status, page, limit: 20 });
      setAiLogs(result.logs);
      setAiLogsTotal(result.total);
    } catch (error) {
      showMessage('Failed to load AI logs', 'error');
    } finally {
      setAiLogsLoading(false);
    }
  }, [isAdmin]);

  const loadAiPrompts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const result = await shortnameService.getAiPrompts();
      setAiPrompts(result);
    } catch (error) {
      showMessage('Failed to load AI prompts', 'error');
    }
  }, [isAdmin]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (activeTab === 'ai-logs' && isAdmin) {
      loadAiLogs(aiLogsSearch, aiLogsStatus, aiLogsPage);
      loadAiPrompts();
    }
  }, [activeTab, isAdmin, aiLogsPage, loadAiLogs, loadAiPrompts]);

  const handleSavePrompt = async (key) => {
    try {
      setSavingPrompt(true);
      await shortnameService.updateAiPrompt(key, editingPromptText);
      showMessage('Prompt updated successfully');
      setEditingPromptKey(null);
      await loadAiPrompts();
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to update prompt', 'error');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleSearchAiLogs = () => {
    setAiLogsPage(1);
    loadAiLogs(aiLogsSearch, aiLogsStatus, 1);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadMainShortnames(),
          loadBranchShortnames(),
          loadCountries()
        ]);
      } catch (error) {
        showMessage(error.response?.data?.error || 'Failed to load data', 'error');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [loadMainShortnames, loadBranchShortnames, loadCountries]);

  const handleSaveMainShortname = async (id) => {
    try {
      setSaving(true);
      await shortnameService.updateMainShortname(id, editingMainValue);
      showMessage('Main shortname updated successfully');
      setEditingMainId(null);
      setEditingMainValue('');
      await loadMainShortnames(mainSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to update main shortname', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMainShortname = async (id, agentName) => {
    if (!window.confirm(`Delete shortname for ${agentName}?`)) return;
    try {
      setSaving(true);
      await shortnameService.deleteMainShortname(id);
      showMessage('Main shortname deleted successfully');
      await loadMainShortnames(mainSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to delete main shortname', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranchShortname = async (id) => {
    try {
      setSaving(true);
      await shortnameService.updateBranchShortname(id, editingBranchValue);
      showMessage('Branch shortname updated successfully');
      setEditingBranchId(null);
      setEditingBranchValue('');
      await loadBranchShortnames(branchSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to update branch shortname', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBranchShortname = async (id, agentName) => {
    if (!window.confirm(`Delete shortname for ${agentName}?`)) return;
    try {
      setSaving(true);
      await shortnameService.deleteBranchShortname(id);
      showMessage('Branch shortname deleted successfully');
      await loadBranchShortnames(branchSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to delete branch shortname', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCountry = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await countryService.createCountry(newCountry);
      showMessage('Country created successfully');
      setNewCountry({ name: '', code: '' });
      await loadCountries();
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to create country', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCountry = async (id) => {
    try {
      setSaving(true);
      await countryService.updateCountry(id, {
        name: editingCountry.name,
        code: editingCountry.code,
        is_active: true
      });
      showMessage('Country updated successfully');
      setEditingCountryId(null);
      setEditingCountry({ name: '', code: '' });
      await loadCountries();
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to update country', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCountry = async (id, countryName) => {
    if (!window.confirm(`Delete country ${countryName}?`)) return;
    try {
      setSaving(true);
      await countryService.deleteCountry(id);
      showMessage('Country deleted successfully');
      await loadCountries();
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to delete country', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateMainShortname = async (id) => {
    try {
      setGeneratingId(id);
      await shortnameService.generateMainShortname(id);
      showMessage('Short name generated by AI successfully');
      await loadMainShortnames(mainSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to generate short name', 'error');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleGenerateBranchShortname = async (id) => {
    try {
      setGeneratingId(id);
      await shortnameService.generateBranchShortname(id);
      showMessage('Short name generated by AI successfully');
      await loadBranchShortnames(branchSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to generate short name', 'error');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleGenerateBatchMain = async () => {
    const missing = mainShortnames.filter(i => !i.short_name || !i.short_name.trim()).length;
    if (!window.confirm(`Generate AI short names for ${missing} agents without one?`)) return;
    try {
      setGeneratingBatch(true);
      const result = await shortnameService.generateBatchMainShortnames();
      showMessage(`Batch done: ${result.success} success, ${result.failed} failed`);
      await loadMainShortnames(mainSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Batch generation failed', 'error');
    } finally {
      setGeneratingBatch(false);
    }
  };

  const handleGenerateBatchBranch = async () => {
    const missing = branchShortnames.filter(i => !i.short_name || !i.short_name.trim()).length;
    if (!window.confirm(`Generate AI short names for ${missing} agents without one?`)) return;
    try {
      setGeneratingBatch(true);
      const result = await shortnameService.generateBatchBranchShortnames();
      showMessage(`Batch done: ${result.success} success, ${result.failed} failed`);
      await loadBranchShortnames(branchSearch);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Batch generation failed', 'error');
    } finally {
      setGeneratingBatch(false);
    }
  };

  const filteredCountries = countries.filter((country) => {
    if (!countrySearch.trim()) return true;
    const q = countrySearch.toLowerCase();
    return (country.name || '').toLowerCase().includes(q) || (country.code || '').toLowerCase().includes(q);
  });

  const paginateData = (data, page, pageSize) => {
    const totalItems = data.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      items: data.slice(startIndex, endIndex),
      totalItems,
      totalPages,
      currentPage: safePage,
      from: totalItems === 0 ? 0 : startIndex + 1,
      to: Math.min(endIndex, totalItems)
    };
  };

  const mainPagination = useMemo(
    () => paginateData(mainShortnames, mainPage, mainPageSize),
    [mainShortnames, mainPage, mainPageSize]
  );

  const branchPagination = useMemo(
    () => paginateData(branchShortnames, branchPage, branchPageSize),
    [branchShortnames, branchPage, branchPageSize]
  );

  const countryPagination = useMemo(
    () => paginateData(filteredCountries, countryPage, countryPageSize),
    [filteredCountries, countryPage, countryPageSize]
  );

  const PaginationControls = ({
    currentPage,
    totalPages,
    totalItems,
    from,
    to,
    pageSize,
    onPageSizeChange,
    onPrev,
    onNext
  }) => (
    <Flex justify="space-between" align="center" wrap="wrap" gap={3} p={4} borderTopWidth="1px" borderColor="gray.100">
      <HStack>
        <Text fontSize="sm" color="gray.600">
          Showing <Text as="span" fontWeight="semibold">{from}</Text>-<Text as="span" fontWeight="semibold">{to}</Text> of <Text as="span" fontWeight="semibold">{totalItems}</Text>
        </Text>
      </HStack>

      <HStack>
        <Text fontSize="sm" color="gray.600">Rows per page</Text>
        <select
          aria-label="Rows per page"
          value={pageSize}
          onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          style={{
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            padding: '6px 8px',
            background: 'white'
          }}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>

        <Text fontSize="sm" color="gray.600">Page {currentPage}/{totalPages}</Text>
        <Button size="sm" variant="outline" onClick={onPrev} disabled={currentPage <= 1}>Prev</Button>
        <Button size="sm" variant="outline" onClick={onNext} disabled={currentPage >= totalPages}>Next</Button>
      </HStack>
    </Flex>
  );

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container display="flex" justifyContent="center" alignItems="center" minH="50vh">
          <Spinner size="xl" color="purple.500" />
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.xl">
        <VStack spacing={6} align="stretch">
          <Box>
            <Heading size="lg" color="gray.800">Master Data Management</Heading>
            <Text color="gray.600">Manage Main/Branch shortname and country code in one interface</Text>
          </Box>

          {message && (
            <Alert.Root status={messageType === 'error' ? 'error' : 'success'}>
              <Alert.Content>{message}</Alert.Content>
            </Alert.Root>
          )}

          <Tabs.Root value={activeTab} onValueChange={(details) => setActiveTab(details.value)}>
            <Tabs.List>
              {canManageMainShortnames && (
                <Tabs.Trigger value="main-shortnames">Shortname Main</Tabs.Trigger>
              )}
              {canManageBranchShortnames && (
                <Tabs.Trigger value="branch-shortnames">Shortname Branch</Tabs.Trigger>
              )}
              {canViewCountries && (
                <Tabs.Trigger value="countries">Country Codes</Tabs.Trigger>
              )}
              {isAdmin && (
                <Tabs.Trigger value="ai-logs"><FaRobot style={{ marginRight: 6 }} />AI Logs</Tabs.Trigger>
              )}
              <Tabs.Indicator />
            </Tabs.List>

            <Tabs.Content value="main-shortnames">
              <Card.Root mt={4}>
                <Card.Header>
                  <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                    <HStack>
                      <Heading size="md">Main Shortname</Heading>
                      <Badge colorPalette="purple">{mainShortnames.length}</Badge>
                    </HStack>
                    <HStack>
                      <Input
                        aria-label="Search main shortname"
                        placeholder="Search main shortname..."
                        value={mainSearch}
                        onChange={(e) => setMainSearch(e.target.value)}
                      />
                      <Button onClick={() => loadMainShortnames(mainSearch)} loading={saving}>Search</Button>
                      {hasRole(['Admin']) && (
                        <Button
                          colorPalette="teal"
                          onClick={handleGenerateBatchMain}
                          loading={generatingBatch}
                          disabled={generatingBatch}
                          size="sm"
                        >
                          <FaMagic /> AI Generate All
                        </Button>
                      )}
                    </HStack>
                  </Flex>
                </Card.Header>
                <Card.Body p={0}>
                  <Box overflowX="auto">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Country</Table.ColumnHeader>
                          <Table.ColumnHeader>Agent Name</Table.ColumnHeader>
                          <Table.ColumnHeader>Short Name</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="center">Actions</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {mainPagination.items.map((item) => (
                          <Table.Row key={item.id}>
                            <Table.Cell>{item.country}</Table.Cell>
                            <Table.Cell>{item.agent_name}</Table.Cell>
                            <Table.Cell>
                              {editingMainId === item.id ? (
                                <Input
                                  size="sm"
                                  value={editingMainValue}
                                  onChange={(e) => setEditingMainValue(e.target.value)}
                                  placeholder="Type shortname"
                                />
                              ) : (
                                item.short_name || <Text color="gray.400">(empty)</Text>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <HStack justify="center">
                                {editingMainId === item.id ? (
                                  <>
                                    <IconButton
                                      aria-label="save"
                                      size="sm"
                                      colorPalette="green"
                                      onClick={() => handleSaveMainShortname(item.id)}
                                      loading={saving}
                                    >
                                      <FaSave />
                                    </IconButton>
                                    <IconButton
                                      aria-label="cancel"
                                      size="sm"
                                      onClick={() => {
                                        setEditingMainId(null);
                                        setEditingMainValue('');
                                      }}
                                    >
                                      <FaTimes />
                                    </IconButton>
                                  </>
                                ) : (
                                  <>
                                    {(!item.short_name || !item.short_name.trim()) && (
                                      <IconButton
                                        aria-label="generate with AI"
                                        size="sm"
                                        colorPalette="teal"
                                        onClick={() => handleGenerateMainShortname(item.id)}
                                        loading={generatingId === item.id}
                                        disabled={generatingId === item.id}
                                        title="Generate with AI"
                                      >
                                        <FaMagic />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      aria-label="edit"
                                      size="sm"
                                      colorPalette="purple"
                                      onClick={() => {
                                        setEditingMainId(item.id);
                                        setEditingMainValue(item.short_name || '');
                                      }}
                                    >
                                      <FaEdit />
                                    </IconButton>
                                    <IconButton
                                      aria-label="delete"
                                      size="sm"
                                      colorPalette="red"
                                      onClick={() => handleDeleteMainShortname(item.id, item.agent_name)}
                                    >
                                      <FaTrash />
                                    </IconButton>
                                  </>
                                )}
                              </HStack>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                  <PaginationControls
                    currentPage={mainPagination.currentPage}
                    totalPages={mainPagination.totalPages}
                    totalItems={mainPagination.totalItems}
                    from={mainPagination.from}
                    to={mainPagination.to}
                    pageSize={mainPageSize}
                    onPageSizeChange={(size) => {
                      setMainPageSize(size);
                      setMainPage(1);
                    }}
                    onPrev={() => setMainPage((prev) => Math.max(1, prev - 1))}
                    onNext={() => setMainPage((prev) => Math.min(mainPagination.totalPages, prev + 1))}
                  />
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            <Tabs.Content value="branch-shortnames">
              <Card.Root mt={4}>
                <Card.Header>
                  <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                    <HStack>
                      <Heading size="md">Branch Shortname</Heading>
                      <Badge colorPalette="purple">{branchShortnames.length}</Badge>
                    </HStack>
                    <HStack>
                      <Input
                        aria-label="Search branch shortname"
                        placeholder="Search branch shortname..."
                        value={branchSearch}
                        onChange={(e) => setBranchSearch(e.target.value)}
                      />
                      <Button onClick={() => loadBranchShortnames(branchSearch)} loading={saving}>Search</Button>
                      {hasRole(['Admin']) && (
                        <Button
                          colorPalette="teal"
                          onClick={handleGenerateBatchBranch}
                          loading={generatingBatch}
                          disabled={generatingBatch}
                          size="sm"
                        >
                          <FaMagic /> AI Generate All
                        </Button>
                      )}
                    </HStack>
                  </Flex>
                </Card.Header>
                <Card.Body p={0}>
                  <Box overflowX="auto">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Country</Table.ColumnHeader>
                          <Table.ColumnHeader>Agent Name</Table.ColumnHeader>
                          <Table.ColumnHeader>Short Name</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="center">Actions</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {branchPagination.items.map((item) => (
                          <Table.Row key={item.id}>
                            <Table.Cell>{item.country}</Table.Cell>
                            <Table.Cell>{item.agent_name}</Table.Cell>
                            <Table.Cell>
                              {editingBranchId === item.id ? (
                                <Input
                                  size="sm"
                                  value={editingBranchValue}
                                  onChange={(e) => setEditingBranchValue(e.target.value)}
                                  placeholder="Type shortname"
                                />
                              ) : (
                                item.short_name || <Text color="gray.400">(empty)</Text>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <HStack justify="center">
                                {editingBranchId === item.id ? (
                                  <>
                                    <IconButton
                                      aria-label="save"
                                      size="sm"
                                      colorPalette="green"
                                      onClick={() => handleSaveBranchShortname(item.id)}
                                      loading={saving}
                                    >
                                      <FaSave />
                                    </IconButton>
                                    <IconButton
                                      aria-label="cancel"
                                      size="sm"
                                      onClick={() => {
                                        setEditingBranchId(null);
                                        setEditingBranchValue('');
                                      }}
                                    >
                                      <FaTimes />
                                    </IconButton>
                                  </>
                                ) : (
                                  <>
                                    {(!item.short_name || !item.short_name.trim()) && (
                                      <IconButton
                                        aria-label="generate with AI"
                                        size="sm"
                                        colorPalette="teal"
                                        onClick={() => handleGenerateBranchShortname(item.id)}
                                        loading={generatingId === item.id}
                                        disabled={generatingId === item.id}
                                        title="Generate with AI"
                                      >
                                        <FaMagic />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      aria-label="edit"
                                      size="sm"
                                      colorPalette="purple"
                                      onClick={() => {
                                        setEditingBranchId(item.id);
                                        setEditingBranchValue(item.short_name || '');
                                      }}
                                    >
                                      <FaEdit />
                                    </IconButton>
                                    <IconButton
                                      aria-label="delete"
                                      size="sm"
                                      colorPalette="red"
                                      onClick={() => handleDeleteBranchShortname(item.id, item.agent_name)}
                                    >
                                      <FaTrash />
                                    </IconButton>
                                  </>
                                )}
                              </HStack>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                  <PaginationControls
                    currentPage={branchPagination.currentPage}
                    totalPages={branchPagination.totalPages}
                    totalItems={branchPagination.totalItems}
                    from={branchPagination.from}
                    to={branchPagination.to}
                    pageSize={branchPageSize}
                    onPageSizeChange={(size) => {
                      setBranchPageSize(size);
                      setBranchPage(1);
                    }}
                    onPrev={() => setBranchPage((prev) => Math.max(1, prev - 1))}
                    onNext={() => setBranchPage((prev) => Math.min(branchPagination.totalPages, prev + 1))}
                  />
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            <Tabs.Content value="countries">
              <Card.Root mt={4}>
                <Card.Header>
                  <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                    <HStack>
                      <Heading size="md">Country Codes</Heading>
                      <Badge colorPalette="blue">{filteredCountries.length}</Badge>
                    </HStack>
                    <Input
                      aria-label="Search country code"
                      maxW="300px"
                      placeholder="Search country/code..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                    />
                  </Flex>
                </Card.Header>
                <Card.Body>
                  {canManageCountries && (
                    <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                      <form onSubmit={handleCreateCountry}>
                        <HStack>
                          <Input
                            placeholder="Country name"
                            value={newCountry.name}
                            onChange={(e) => setNewCountry((prev) => ({ ...prev, name: e.target.value }))}
                            required
                            bg="white"
                          />
                          <Input
                            placeholder="Code"
                            value={newCountry.code}
                            onChange={(e) => setNewCountry((prev) => ({ ...prev, code: e.target.value }))}
                            bg="white"
                          />
                          <Button type="submit" colorPalette="blue" loading={saving}>Add</Button>
                        </HStack>
                      </form>
                    </Box>
                  )}

                  <Box overflowX="auto">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Country</Table.ColumnHeader>
                          <Table.ColumnHeader>Code</Table.ColumnHeader>
                          <Table.ColumnHeader>Status</Table.ColumnHeader>
                          {canManageCountries && <Table.ColumnHeader textAlign="center">Actions</Table.ColumnHeader>}
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {countryPagination.items.map((country) => (
                          <Table.Row key={country.id}>
                            <Table.Cell>
                              {editingCountryId === country.id ? (
                                <Input
                                  size="sm"
                                  value={editingCountry.name}
                                  onChange={(e) => setEditingCountry((prev) => ({ ...prev, name: e.target.value }))}
                                />
                              ) : (
                                country.name
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {editingCountryId === country.id ? (
                                <Input
                                  size="sm"
                                  value={editingCountry.code || ''}
                                  onChange={(e) => setEditingCountry((prev) => ({ ...prev, code: e.target.value }))}
                                />
                              ) : (
                                country.code || '-'
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge colorPalette={country.is_active ? 'green' : 'gray'}>
                                {country.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </Table.Cell>
                            {canManageCountries && (
                              <Table.Cell>
                                <HStack justify="center">
                                  {editingCountryId === country.id ? (
                                    <>
                                      <IconButton
                                        aria-label="save"
                                        size="sm"
                                        colorPalette="green"
                                        onClick={() => handleUpdateCountry(country.id)}
                                        loading={saving}
                                      >
                                        <FaSave />
                                      </IconButton>
                                      <IconButton
                                        aria-label="cancel"
                                        size="sm"
                                        onClick={() => {
                                          setEditingCountryId(null);
                                          setEditingCountry({ name: '', code: '' });
                                        }}
                                      >
                                        <FaTimes />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <>
                                      <IconButton
                                        aria-label="edit"
                                        size="sm"
                                        colorPalette="purple"
                                        onClick={() => {
                                          setEditingCountryId(country.id);
                                          setEditingCountry({
                                            name: country.name || '',
                                            code: country.code || ''
                                          });
                                        }}
                                      >
                                        <FaEdit />
                                      </IconButton>
                                      <IconButton
                                        aria-label="delete"
                                        size="sm"
                                        colorPalette="red"
                                        onClick={() => handleDeleteCountry(country.id, country.name)}
                                      >
                                        <FaTrash />
                                      </IconButton>
                                    </>
                                  )}
                                </HStack>
                              </Table.Cell>
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                  <PaginationControls
                    currentPage={countryPagination.currentPage}
                    totalPages={countryPagination.totalPages}
                    totalItems={countryPagination.totalItems}
                    from={countryPagination.from}
                    to={countryPagination.to}
                    pageSize={countryPageSize}
                    onPageSizeChange={(size) => {
                      setCountryPageSize(size);
                      setCountryPage(1);
                    }}
                    onPrev={() => setCountryPage((prev) => Math.max(1, prev - 1))}
                    onNext={() => setCountryPage((prev) => Math.min(countryPagination.totalPages, prev + 1))}
                  />
                </Card.Body>
              </Card.Root>
            </Tabs.Content>
            {isAdmin && (
            <Tabs.Content value="ai-logs">
              <Card.Root mt={4}>
                <Card.Header>
                  <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                    <HStack>
                      <Heading size="md">AI Generation Logs</Heading>
                      <Badge colorPalette="purple">{aiLogsTotal}</Badge>
                    </HStack>
                    <HStack>
                      <Button
                        size="sm"
                        variant={showPromptEditor ? 'solid' : 'outline'}
                        colorPalette="teal"
                        onClick={() => setShowPromptEditor(!showPromptEditor)}
                      >
                        {showPromptEditor ? 'Hide Prompts' : 'Edit Prompts'}
                      </Button>
                    </HStack>
                  </Flex>
                </Card.Header>
                <Card.Body>
                  {/* Prompt Editor */}
                  {showPromptEditor && (
                    <Box mb={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                      {/* Short Name Prompts */}
                      <Heading size="sm" mb={1} color="teal.700">Short Name Generation</Heading>
                      <Text fontSize="xs" color="gray.500" mb={3}>Controls how AI generates abbreviated agent names</Text>
                      {aiPrompts.filter(p => p.prompt_key.startsWith('shortname_')).map((prompt) => (
                        <Box key={prompt.prompt_key} mb={4}>
                          <Text fontWeight="bold" mb={1}>
                            {prompt.prompt_key === 'shortname_system' ? 'System Prompt' : 'User Prompt Template'}
                          </Text>
                          {editingPromptKey === prompt.prompt_key ? (
                            <VStack align="stretch">
                              <textarea
                                value={editingPromptText}
                                onChange={(e) => setEditingPromptText(e.target.value)}
                                rows={prompt.prompt_key === 'shortname_system' ? 10 : 3}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  fontFamily: 'monospace',
                                  fontSize: '13px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  resize: 'vertical',
                                }}
                              />
                              {prompt.prompt_key === 'shortname_user' && (
                                <Text fontSize="xs" color="gray.500">
                                  Use {'${agentName}'} as placeholder for agent name
                                </Text>
                              )}
                              <HStack>
                                <Button
                                  size="sm"
                                  colorPalette="green"
                                  onClick={() => handleSavePrompt(prompt.prompt_key)}
                                  loading={savingPrompt}
                                >
                                  <FaSave /> Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingPromptKey(null)}
                                >
                                  Cancel
                                </Button>
                              </HStack>
                            </VStack>
                          ) : (
                            <Box
                              p={2}
                              bg="white"
                              borderWidth="1px"
                              borderRadius="sm"
                              fontFamily="monospace"
                              fontSize="13px"
                              whiteSpace="pre-wrap"
                              cursor="pointer"
                              onClick={() => {
                                setEditingPromptKey(prompt.prompt_key);
                                setEditingPromptText(prompt.prompt_text);
                              }}
                              _hover={{ borderColor: 'teal.300' }}
                            >
                              {prompt.prompt_text}
                            </Box>
                          )}
                          {prompt.updated_by && (
                            <Text fontSize="xs" color="gray.400" mt={1}>
                              Last updated by {prompt.updated_by} at {new Date(prompt.updated_at).toLocaleString()}
                            </Text>
                          )}
                        </Box>
                      ))}

                      {/* Vision Extract Prompts */}
                      <Separator my={4} />
                      <Heading size="sm" mb={1} color="purple.700">Vision Card Extraction</Heading>
                      <Text fontSize="xs" color="gray.500" mb={3}>Controls how AI extracts data from business card images</Text>
                      {aiPrompts.filter(p => p.prompt_key.startsWith('vision_')).map((prompt) => (
                        <Box key={prompt.prompt_key} mb={4}>
                          <Text fontWeight="bold" mb={1}>
                            System Prompt (Vision)
                          </Text>
                          {editingPromptKey === prompt.prompt_key ? (
                            <VStack align="stretch">
                              <textarea
                                value={editingPromptText}
                                onChange={(e) => setEditingPromptText(e.target.value)}
                                rows={10}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  fontFamily: 'monospace',
                                  fontSize: '13px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  resize: 'vertical',
                                }}
                              />
                              <HStack>
                                <Button
                                  size="sm"
                                  colorPalette="green"
                                  onClick={() => handleSavePrompt(prompt.prompt_key)}
                                  loading={savingPrompt}
                                >
                                  <FaSave /> Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingPromptKey(null)}
                                >
                                  Cancel
                                </Button>
                              </HStack>
                            </VStack>
                          ) : (
                            <Box
                              p={2}
                              bg="white"
                              borderWidth="1px"
                              borderRadius="sm"
                              fontFamily="monospace"
                              fontSize="13px"
                              whiteSpace="pre-wrap"
                              cursor="pointer"
                              onClick={() => {
                                setEditingPromptKey(prompt.prompt_key);
                                setEditingPromptText(prompt.prompt_text);
                              }}
                              _hover={{ borderColor: 'purple.300' }}
                            >
                              {prompt.prompt_text}
                            </Box>
                          )}
                          {prompt.updated_by && (
                            <Text fontSize="xs" color="gray.400" mt={1}>
                              Last updated by {prompt.updated_by} at {new Date(prompt.updated_at).toLocaleString()}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Search & Filter */}
                  <HStack mb={4} gap={3}>
                    <Input
                      placeholder="Search by agent name..."
                      value={aiLogsSearch}
                      onChange={(e) => setAiLogsSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchAiLogs()}
                      maxW="300px"
                    />
                    <select
                      value={aiLogsStatus}
                      onChange={(e) => {
                        setAiLogsStatus(e.target.value);
                        setAiLogsPage(1);
                        loadAiLogs(aiLogsSearch, e.target.value, 1);
                      }}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ccc' }}
                    >
                      <option value="all">All Status</option>
                      <option value="ok">OK</option>
                      <option value="error">Error</option>
                    </select>
                    <Button size="sm" onClick={handleSearchAiLogs}>Search</Button>
                  </HStack>

                  {/* Logs Table */}
                  {aiLogsLoading ? (
                    <Flex justify="center" py={8}><Spinner /></Flex>
                  ) : (
                    <Box overflowX="auto">
                      <Table.Root size="sm" variant="outline">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader>Time</Table.ColumnHeader>
                            <Table.ColumnHeader>Table</Table.ColumnHeader>
                            <Table.ColumnHeader>Agent Name</Table.ColumnHeader>
                            <Table.ColumnHeader>AI Output (raw)</Table.ColumnHeader>
                            <Table.ColumnHeader>Final</Table.ColumnHeader>
                            <Table.ColumnHeader>Model</Table.ColumnHeader>
                            <Table.ColumnHeader>Duration</Table.ColumnHeader>
                            <Table.ColumnHeader>Status</Table.ColumnHeader>
                            <Table.ColumnHeader>Editor</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {aiLogs.length === 0 ? (
                            <Table.Row>
                              <Table.Cell colSpan={9} textAlign="center" py={4} color="gray.500">
                                No AI logs found
                              </Table.Cell>
                            </Table.Row>
                          ) : (
                            aiLogs.map((log) => (
                              <Table.Row key={log.id}>
                                <Table.Cell whiteSpace="nowrap" fontSize="xs">
                                  {new Date(log.created_at).toLocaleString()}
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge colorPalette={log.table_type === 'main' ? 'blue' : 'purple'} size="sm">
                                    {log.table_type}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell maxW="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={log.agent_name}>
                                  {log.agent_name}
                                </Table.Cell>
                                <Table.Cell maxW="150px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={log.raw_response}>
                                  {log.raw_response || '-'}
                                </Table.Cell>
                                <Table.Cell fontWeight="bold">
                                  {log.final_short_name || '-'}
                                </Table.Cell>
                                <Table.Cell fontSize="xs">{log.model}</Table.Cell>
                                <Table.Cell fontSize="xs">
                                  {log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : '-'}
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge colorPalette={log.status === 'ok' ? 'green' : 'red'} size="sm">
                                    {log.status === 'ok' ? 'OK' : 'ERR'}
                                  </Badge>
                                  {log.error_message && (
                                    <Text fontSize="xs" color="red.500" title={log.error_message}>
                                      {log.error_message.substring(0, 30)}...
                                    </Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell fontSize="xs">{log.editor || '-'}</Table.Cell>
                              </Table.Row>
                            ))
                          )}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  )}

                  {/* Pagination */}
                  {aiLogsTotal > 20 && (
                    <Flex justify="center" align="center" mt={4} gap={3}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={aiLogsPage <= 1}
                        onClick={() => setAiLogsPage((p) => p - 1)}
                      >
                        Prev
                      </Button>
                      <Text fontSize="sm">
                        Page {aiLogsPage} / {Math.ceil(aiLogsTotal / 20)}
                      </Text>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={aiLogsPage >= Math.ceil(aiLogsTotal / 20)}
                        onClick={() => setAiLogsPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </Flex>
                  )}
                </Card.Body>
              </Card.Root>
            </Tabs.Content>
            )}
          </Tabs.Root>
        </VStack>
      </Container>
    </Box>
  );
};

export default MasterDataManager;
